<template>
    <b-card-code
      title="Validators Active Set (updated to next epoch)"
      no-body
    >
      <b-card-body>
        <div class="d-flex justify-content-between flex-wrap">
  
          <!-- sorting  -->
          <b-form-group
            class="mr-1 mb-md-0"
          >
            <b-input-group
              size="sm"
            >
              <b-form-select
                id="sortBySelect"
                v-model="sortBy"
                :options="sortOptions"
              >
                <template #first>
                  <option value="">
                    Sort
                  </option>
                </template>
              </b-form-select>
              <b-form-select
                v-model="sortDesc"
                size="sm"
                :disabled="!sortBy"
              >
                <option :value="false">
                  Asc
                </option>
                <option :value="true">
                  Desc
                </option>
              </b-form-select>
            </b-input-group>
          </b-form-group>
  
          <!-- filter -->
          <b-form-group
            class="mb-0"
          >
            <b-input-group size="sm">
              <b-form-input
                id="filterInput"
                v-model="filter"
                type="search"
                placeholder="Type to Search"
              />
              <b-input-group-append>
                <b-button
                  :disabled="!filter"
                  @click="filter = ''"
                >
                  Clear
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </div>
      </b-card-body>
  
      <b-table
        v-if="loaded"
        hover
        responsive
        class="position-relative text-center"
        :per-page="perPage"
        :current-page="currentPage"
        :items="items"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-compare="sortCompare"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        :filter="filter"
        :filter-included-fields="filterOn"
        @filtered="onFiltered"
      >
      <template #cell(validator)="data">
        <b-link target="_blank" :href="`${data.item.url}`">
            {{ data.value }}
        </b-link>
      </template>
      <template #cell(stake)="data">
        {{ Intl.NumberFormat('en-US').format((data.value).toFixed(2)) + ' Ⓝ' }}
      </template>
      <template #cell(power)="data">
        {{ data.value.toFixed(2)+'%' }}
      </template>
      <template #cell(blocks)="data">
        {{ data.value > 0 ? Intl.NumberFormat('en-US').format((data.value)) : data.value }}
      </template>
      <template #cell(cumulative_stake)="data">
        {{ data.value.toFixed(2)+'%' }}
      </template>
      
      </b-table>
  
      <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
  
        <!-- page length -->
        <b-form-group
          label="Per Page"
          label-cols="6"
          label-align="left"
          label-size="sm"
          label-for="sortBySelect"
          class="text-nowrap mb-md-0 mr-1"
        >
          <b-form-select
            id="perPageSelect"
            v-model="perPage"
            size="sm"
            inline
            :options="pageOptions"
          />
        </b-form-group>
  
        <!-- pagination -->
        <div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            class="mb-0"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </div>
      </b-card-body>
  
      <template #code>
        {{ codeKitchenSink }}
      </template>
    </b-card-code>
  </template>
  
  <script>
  import BCardCode from '@core/components/b-card-code/BCardCode.vue'
  import {
    BTable, BLink, BAvatar, BBadge, BFormGroup, BFormSelect, BPagination, BInputGroup, BFormInput, BInputGroupAppend, BButton, BCardBody,
  } from 'bootstrap-vue'
  import { codeKitchenSink, collections } from './code'
  import axios from 'axios'
  
  export default {
    components: {
      BCardCode,
      BTable,
      BAvatar,
      BBadge,
      BFormGroup,
      BFormSelect,
      BPagination,
      BInputGroup,
      BFormInput,
      BLink,
      BInputGroupAppend,
      BButton,
      BCardBody,
    },
    data() {
      return {
        loaded: false,
        perPage: 10,
        pageOptions: [10, 15, 20],
        totalRows: 1,
        currentPage: 1,
        sortBy: '',
        sortDesc: false,
        sortDirection: 'asc',
        filter: null,
        filterOn: [],
        infoModal: {
          id: 'info-modal',
          title: '',
          content: '',
        },
        fields: [
          { key: 'id', label: 'Id' },                                        
          { key: 'validator', label: 'Validator', sortable: true },
          { key: 'stake', label: 'Stake', sortable: true, tdClass: 'font-weight-bold' },
          { key: 'blocks', label: 'Blocks Produced', sortable: true},
          { key: 'epochs', label: 'Epochs in the Active Set'},
          { key: 'power', label: 'Power %'},
          { key: 'cumulative_stake', label: 'Cumulative Stake %'},
        ],
        /* eslint-disable global-require */
        items: [
        ],
        codeKitchenSink,
        collections,
      }
    },
    computed: {
      sortOptions() {
        // Create an options list from our fields
        return this.fields
          .filter(f => f.sortable)
          .map(f => ({ text: f.label, value: f.key }))
      },
    },
    mounted() {
      this.loaded = false;
      const endpoint = 'https://node-api.flipsidecrypto.com/api/v2/queries/936f41d9-9669-4b70-b3af-b4e6f49b5b01/data/latest'
      axios.get(endpoint)
      .then(response => {
        var i = 1;
        response.data.forEach(validator => {
          this.items.push({
            id: i++,
            validator: validator.VALIDATOR,
            stake: validator.STAKED,
            power: validator.POWER_PERCENTAGE,
            cumulative_stake: validator.CUMULATIVE_STAKE_PERC,
            url: `https://nearblocks.io/address/${validator.VALIDATOR}`,
            blocks: 0,
            epochs: 0
          })
        })
        const add = 'https://node-api.flipsidecrypto.com/api/v2/queries/f02be878-3b4b-49fc-90b0-03c13e06bf25/data/latest'
        axios.get(add)
        .then(response => {
          for (let val in response.data){
            var item = this.items.find(x => x.validator == response.data[val].VALIDATOR) || undefined
            if(item){
              item.blocks = response.data[val].TOT_BLOCK_CREATED
              item.epochs = response.data[val].EPOCH_PARTAKEN
            }
          }
          this.loaded = true;
          this.totalRows = this.items.length
        })
        .catch(e => {
          console.error(e)
        })
      })

    },
    methods: {
      info(item, index, button) {
        this.infoModal.title = `Row index: ${index}`
        this.infoModal.content = JSON.stringify(item, null, 2)
        this.$root.$emit('bv::show::modal', this.infoModal.id, button)
      },
      resetInfoModal() {
        this.infoModal.title = ''
        this.infoModal.content = ''
      },
      onFiltered(filteredItems) {
        // Trigger pagination to update the number of buttons/pages due to filtering
        this.totalRows = filteredItems.length
        this.currentPage = 1
      },
      sortCompare(
        aRow,
        bRow,
        key,
        sortDesc,
        formatter,
        compareOptions,
        compareLocale
      ) {
        if(key == 'floor_price')
          return aRow[key].split(' ')[0].localeCompare(bRow[key].split(' ')[0], compareLocale, compareOptions);
      }
      },
  }
  </script>
  